import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import styles from './CommentsThread.module.scss';
import { useOutsideClick } from '../../../utils/constants/hooks';
import { Editor, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

interface MentionUser {
    name: string;
    userId: string;
    emailId?: string;
}

interface MentionInputProps {
    editor: any;
    mentionsActive: boolean;
    setMentionsActive: Function;
    mentionedUsers: any;
    setMentionedUsers: any;

    currentMentionWord: string | null;
    placeholder?: string;
    mentionOptions: MentionUser[];
    id?: string;
    className?: string;
}

const MentionInput: React.FC<MentionInputProps> = ({
    editor,
    mentionsActive,
    setMentionsActive,
    setMentionedUsers,

    currentMentionWord,
    mentionOptions,
}) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const mentionDropdownRef = useRef<HTMLDivElement>(null);

    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const [filteredMentions, setFilteredMentions] = useState<MentionUser[]>([]);

    const wrapperRef = useRef<HTMLDivElement>(null);

    // function to ensure cursor stays visible
    const ensureCursorVisible = useCallback(() => {
        if (inputRef.current) {
            const textarea = inputRef.current;
            if (textarea.scrollHeight > textarea.clientHeight) {
                textarea.scrollTop = textarea.scrollHeight;
            }
        }
    }, []);

    useEffect(() => {
        if (currentMentionWord) {
            setFilteredMentions(getFilteredMentions(currentMentionWord?.replace('@', '')));
        }
    }, [currentMentionWord]);

    const getFilteredMentions = useCallback(
        (search: string) => {
            if (!search.trim()) return mentionOptions.slice(0, 10);

            const lowerSearch = search.toLowerCase();
            const startsWithMatches = mentionOptions.filter((option) => option.name?.toLowerCase().startsWith(lowerSearch));
            const containsMatches = mentionOptions.filter(
                (option) => !option.name?.toLowerCase().startsWith(lowerSearch) && option.name?.toLowerCase().includes(lowerSearch),
            );
            // Combine both lists with startsWith matches first
            return [...startsWithMatches, ...containsMatches];
        },
        [mentionOptions],
    );

    useOutsideClick(wrapperRef, () => {
        if (mentionsActive) {
            setMentionsActive(null);
        }
    });

    // Scroll to selected mention when navigating with arrow keys
    useEffect(() => {
        if (mentionsActive && mentionDropdownRef.current && filteredMentions.length > 0) {
            const selectedElement = mentionDropdownRef.current.querySelector(`.${styles.Selected}`);
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [selectedMentionIndex, mentionsActive, filteredMentions.length]);

    const insertMention = useCallback(
        (userName: string, userId: string) => {
            if (!editor || !currentMentionWord) return;
            const { selection } = editor;
            if (!selection) return;

            try {
                const [point] = Range.edges(selection);
                const beforeText = Editor.before(editor, point);
                const beforeRange = beforeText && Editor.range(editor, Editor.start(editor, []), point);
                const textBeforeCursor = beforeRange ? Editor.string(editor, beforeRange) : '';
                const lastAtIndex = textBeforeCursor.lastIndexOf('@');
                if (lastAtIndex === -1) return;

                const mentionStartPoint = {
                    path: point.path,
                    offset: point.offset - (textBeforeCursor.length - lastAtIndex),
                };

                const replaceRange = Editor.range(editor, mentionStartPoint, point);
                const mentionText = `@${userName}`;
                const marks = Editor.marks(editor) || {};
                const cleanMarks: any = { ...marks };
                delete cleanMarks.mention;
                delete cleanMarks.mentionUserId;
                delete cleanMarks.mentionUserName;

                Editor.withoutNormalizing(editor, () => {
                    Transforms.select(editor, replaceRange);
                    Transforms.delete(editor, { at: replaceRange });
                    const mentionTextNode = {
                        text: mentionText,
                        mention: true,
                        mentionUserId: userId,
                        mentionUserName: userName,
                        ...marks,
                    };
                    const spaceTextNode = {
                        text: '  ',
                        ...cleanMarks, // Only original formatting, no mention properties
                    };

                    // Insert the custom text node
                    Transforms.insertNodes(editor, [mentionTextNode, spaceTextNode], {
                        at: mentionStartPoint,
                    });
                    Transforms.move(editor, { unit: 'offset', distance: mentionText.length + 3 });

                    // Clear mention marks from editor state before any future operations
                    Editor.removeMark(editor, 'mention');
                    Editor.removeMark(editor, 'mentionUserId');
                    Editor.removeMark(editor, 'mentionUserName');

                    // Reapply only clean marks for future typing
                    Object.entries(cleanMarks).forEach(([key, value]) => {
                        if (value !== null && value !== undefined) {
                            Editor.addMark(editor, key, value);
                        }
                    });
                });

                setMentionedUsers((prev) => {
                    if (!prev.includes(userId)) {
                        return [...prev, userId];
                    }
                    return prev;
                });

                // Close the mentions dropdown
                setMentionsActive(null);

                // Focus back to editor
                ReactEditor.focus(editor);
            } catch (error) {
                console.error('Error inserting mention:', error);
            }
        },
        [mentionStartIndex, currentMentionWord, ensureCursorVisible],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev < filteredMentions.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
                    break;
                case 'Enter':
                case 'Tab':
                    e.preventDefault();
                    if (filteredMentions[selectedMentionIndex]) {
                        const selected = filteredMentions[selectedMentionIndex];
                        insertMention(selected.name, selected.userId);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setMentionsActive(null);
                    break;
            }
        },
        [mentionsActive, filteredMentions, selectedMentionIndex, insertMention, ensureCursorVisible],
    );

    useEffect(() => {
        if (mentionsActive) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [handleKeyDown, mentionsActive]);

    const mentionsDropdown = useMemo(() => {
        if (!mentionsActive) return null;

        return (
            <div className={styles.MentionsDropdown} ref={mentionDropdownRef}>
                {filteredMentions.length === 0 ? (
                    <div className={styles.MentionOption}>No users found</div>
                ) : (
                    filteredMentions.map((option, index) => (
                        <div
                            key={option.userId || index}
                            className={clsx(styles.MentionOption, {
                                [styles.Selected]: index === selectedMentionIndex,
                            })}
                            onClick={() => insertMention(option.name, option.userId)}
                            onMouseEnter={() => setSelectedMentionIndex(index)}
                        >
                            <span className={styles.MentionName}>{option.name}</span>
                            {option.emailId && <span className={styles.MentionEmail}>{option.emailId}</span>}
                        </div>
                    ))
                )}
            </div>
        );
    }, [mentionsActive, filteredMentions, selectedMentionIndex, insertMention]);

    return mentionsDropdown;
};

export default React.memo(MentionInput);
